import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from "react";
import useFormWithSchema from "../../hooks/useFormWithSchema";
import * as Yup from "yup";
import { Controller } from "react-hook-form";
import Accordian from "../accordian";
import { useTranslation } from "react-i18next";
import TextInput from "../inputs/TextInput";
import { formattedDate } from "../../lib/functions";
import { EXPORT_PAGE_SIZE } from "../../constants/common";
import { useGetAllRatingsReport } from "../../hooks/report/query";
import {
  ExportRatingsReportForExcelPDFBody,
  Rating,
} from "../../hooks/report/interface";
import { Button } from "../button";

interface SearchRatingsReportCardProps {
  setAllRatings: Dispatch<SetStateAction<Rating[]>>;
  page: number;
  setPages: Dispatch<SetStateAction<number>>;
  search: string;
  setCount: Dispatch<SetStateAction<number>>;
  setExportBody: Dispatch<
    SetStateAction<ExportRatingsReportForExcelPDFBody | undefined>
  >;
  pageSize: number;
  setPage: Dispatch<SetStateAction<number>>;
}

const SearchRatingsReportCardSchema = Yup.object({
  fromDate: Yup.string(),
  toDate: Yup.string(),
});

const initialState = {
  fromDate: "",
  toDate: "",
};

const SearchRatingsReportCard = ({
  setAllRatings,
  page,
  setPages,
  search,
  setCount,
  setExportBody,
  pageSize,
  setPage,
}: SearchRatingsReportCardProps) => {
  const { control, watch, reset } = useFormWithSchema(
    SearchRatingsReportCardSchema,
    {
      defaultValues: {
        fromDate: formattedDate(new Date()),
        toDate: formattedDate(new Date()),
      },
    }
  );

  const { t } = useTranslation();

  const [selectedKey, setSelectedKey] = useState<string>("");

  const fromDate = watch("fromDate");
  const toDate = watch("toDate");

  const { data: ratings } = useGetAllRatingsReport({
    pageSize: pageSize,
    pageNumber: page,
    search: search,
    fromDate: fromDate,
    toDate: toDate,
  });

  useEffect(() => {
    if (ratings) {
      setAllRatings(ratings.data);
      setPages(ratings.pagination?.pages || 1);
      setCount(ratings.pagination?.count || 0);
    }
  }, [setAllRatings, setPages, ratings, setCount]);

  useEffect(() => {
    setExportBody({
      pageSize: EXPORT_PAGE_SIZE,
      pageNumber: page,
      search: search,
      fromDate: fromDate,
      toDate: toDate,
    });
  }, [page, search, fromDate, toDate, setExportBody]);

  useEffect(() => {
    setPage(1);
  }, [fromDate, toDate, setPage]);

  const onClear = useCallback(() => {
    reset(initialState);
  }, [reset]);

  return (
    <div className="bg-white p-2 md:p-4 rounded-xl flex flex-col gap-3 lg:gap-6 shadow-[0px_1px_5px_0px_rgba(113,112,163,0.3)]">
      <Accordian
        id="1"
        selectedKey={selectedKey}
        onSelectionChange={setSelectedKey}
        title={
          <h1 className="text-xl md:text-2xl flex flex-1 -mt-2">
            {t("searchFilters")}
          </h1>
        }
      >
        <div className="flex flex-col gap-4">
          <form className="flex flex-col gap-4">
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-2 lg:gap-4">
              <Controller
                name="fromDate"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <TextInput
                    type="date"
                    label={t("fromDate")}
                    value={value}
                    onChange={(e) => {
                      onChange(e.target.value);
                    }}
                  />
                )}
              />

              <Controller
                name="toDate"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <TextInput
                    type="date"
                    label={t("toDate")}
                    value={value}
                    onChange={(e) => {
                      onChange(e.target.value);
                    }}
                  />
                )}
              />
            </div>
          </form>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              onClick={onClear}
              className="bg-transparent ring-1 ring-purple-primary text-purple-primary hover:bg-transparent"
            >
              {t("clear")}
            </Button>
          </div>
        </div>
      </Accordian>
    </div>
  );
};

export default React.memo(SearchRatingsReportCard);
